import { Component, OnInit, ChangeDetectorRef, ViewChild, OnDestroy } from '@angular/core';
import { FlexBasePage } from '@wephone-core-ui';
import { _tk, _ti } from '@wephone-translation';
import * as _ from 'lodash';
import {
  IFlexTableConfig, DialogService, MessageService, FlexTableComponent, localNow, parseDateTime, FormService,
} from '@wephone-utils';
import { FlexDataSource } from 'src/app/wephone-utils/datasource/datasource';
import { DataSourceService } from '@wephone-core/service/datasource.service';
import { DateTime } from 'luxon';
import { EntityManager } from '@wephone-core/wephone-core.module';
// import { MyCallCenterStatusRepository } from '@wephone-core/model/repository/my_callcenter_status';
import { PhoneService } from '@wephone-app-phone/services';
import { CallLogDetail } from '@wephone-common/call-lists/calllog-detail/calllog-detail';
import { CallDirection, ICallLog } from '../history/calllog.i';
import { VoiceMailRepository } from '@wephone-core/model/repository/voicemail';
import {
  MissedCallData, MissedCallDataMsg, RecallState, RecallStateText,
} from '@wephone-core/model/entity/missed_call.i';
import { NavigationEnd, Router, RouterEvent } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FlexDatepickerMin, PhoneNumberValidated } from '@wephone/services/form-validator';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { CallQueueRepository } from '@wephone-core/model/repository/callqueue';
import { AgentPanelService } from '@wephone/services/agent-panel.service';
import { AuthenticationService } from '@wephone-core/service/authentication';

interface ToDoCallItem {
  id: number;
  last_inbound_cdr_id: number;
  message_id: number;
  queue_id: number;
  is_read: number; // For last voicemail
  file_entry_ready: number; // For last voicemail
  missed_call_count: number;
  readers: number[]; // For last voicemail
  start_dt: string;
  recall_state: number;
  recall_request_status: number;
  recall_time: string;
  calling_number: string;
  called_number: string;
  queue_name: string;
  agent_name: string;
  recall_dt: string;
  recall_count: number;
  status: number;
  sound_id: string;
  action: string;
}

@Component({
  selector: 'app-missed-call-phone',
  templateUrl: './missed-call.component.html',
  styleUrls: ['./missed-call.component.scss']
})
export class MissedCallComponent extends FlexBasePage implements OnInit, OnDestroy {
  @ViewChild('flexTable', { static: false }) flexTable: FlexTableComponent;

  tableConfig: IFlexTableConfig = {
    rowHeight: 40,
    focusedRowHeight: 80,
    multiSelect: false,
    enableFilter: false,
    enableExportCsv: false,
    columns: [
      // { name: 'sound_id', label: 'Direction', width: '50px' },
      { name: 'dest_name', label: _tk('stats.missed_call.columns.dest_name'), visible: true },
      // { name: 'queue_name', label: _tk('stats.missed_call.columns.queue') }, // Call number, queue name
      // { name: 'start_dt', label: _tk('call_queue.content.time'), width: '90px', align: 'right' }, // Access time
      // { name: 'status', label: _tk('stats.missed_call.columns.status'), width: '120px', align: 'right' },
    ]
  };

  dataSource: FlexDataSource;
  filterReady: boolean;
  filterForm: FormGroup;

  private queueRepo: CallQueueRepository;
  private userId: number;

  @BlockUI('flex-grid-block') blockUIList: NgBlockUI;
  constructor(
    private readonly dialogService: DialogService,
    private readonly phoneService: PhoneService,
    private readonly agentPanelService: AgentPanelService,
    private readonly authService: AuthenticationService,
    private readonly datasourceService: DataSourceService,
    private readonly em: EntityManager,
    private readonly messageService: MessageService,
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private readonly formService: FormService,
    cdr: ChangeDetectorRef,
  ) {
    super(cdr);
    this.dataSource = this.datasourceService.createDataSource('callcenter_todo_call_list');
    this.dataSource.setOrder('start_dt', 'desc');

    this.queueRepo = this.em.getRepository<CallQueueRepository>('CallQueueRepository');
    this.userId = this.authService.getUserId();
  }

  ngOnInit(): void {
    super.ngOnInit();
    // this.loadDataSource();

    // Subscribe to updata missed-call if receive message update_status
    this.addSubscription(
      this.messageService.subscribeMissedCallStatus((msg: MissedCallDataMsg) => {
        // this.loggerService.log('subscribeMissedCallStatus data', msg);
        this.updateMissedCall(msg.data);
      })
    );

    // Reload if router redirect from another page
    this.addSubscription(
      this.router.events.subscribe(
        (event: RouterEvent) => {
          if (event instanceof NavigationEnd && event.urlAfterRedirects === '/dashboard?view=callhistory') {
            this.loadDataSource();
          }
        }
      )
    );
  }

  protected resolveData(): Promise<any> {
    const today: DateTime = localNow();
    this.filterForm = this.fb.group({
      start_dt: [today.minus({ days: 7 }).toJSDate()],
      end_dt: [today.toJSDate()],
      queue_ids: [undefined],
      phone_number: [undefined, [PhoneNumberValidated(true), Validators.maxLength(16)]],
    }, { validator: FlexDatepickerMin('end_dt', 'start_dt') });

    return this.loadDataSource();
  }

  /******************
  * Cannot bind input [min] because there's a bug with Mat-Datepicker,
  * the Form error won't fire if the binding object's value was changed.
  ******************/
  minEndDateFilter = (d: Date): boolean => {
    const startDate = this.filterForm.get('start_dt').value;
    if (!d || !startDate) {
      return true;
    }

    const minDate = parseDateTime(startDate)?.startOf('day');
    const dt = parseDateTime(d)?.startOf('day');
    return minDate && dt && minDate.diff(dt).as('days') > 0 ? false : true;
  }

  async loadDataSource(): Promise<void> {
    try {
      const message = _ti('public.message.loading');
      this.blockUIList.start(message);

      // Hide show reload link if exist
      if (this.flexTable) {
        this.flexTable.showReloadLink = false;
      }

      const filters: any = this.getFilters();

      if (filters && filters.start_dt && filters.end_dt) {
        _.extend(filters, {
          start_dt: filters.start_dt.toISODate(),
          end_dt: filters.end_dt.toISODate(),
        });
      }

      if (this.flexTable) {
        await this.flexTable.reloadDataSource(filters);
        return;
      }

      await this.dataSource.load(filters);
    } catch (error) {
      this.loggerService.error('load data error', error);
    } finally {
      this.blockUIList.stop();
    }
  }

  private updateMissedCall(data: MissedCallData): void {
    // this.loggerService.log('update missed call', data);
    const item = this.dataSource.data && this.dataSource.data.find(x => x.id === data.id);
    if (!item) {
      this.loggerService.warn('Missed call not found from local', data);
      this.dataSource.notifyNewItem(data);
      return;
    }

    if (data.finished) {
      this.dataSource.deleteRow(item);
    } else {
      // Update state
      item.status = RecallStateText[data.recall_state || RecallState.TO_TRY];

      // Update handled-agent
      item.owner_id = data.owner_id;
      item.last_outbound_user_id = data.last_outbound_user_id;
      item.missed_call_count = data.missed_call_count;
      item.recall_request_status = data.recall_request_status;
    }
  }

  onMakeCall = (phoneNumber: string, call: any) => {
    // this.loggerService.log('Calling number', phoneNumber);
    this.makeCall(call);
    return;
  }

  detailMissedCall(call: ToDoCallItem): void {
    const callLog: ICallLog = _.extend(_.cloneDeep(call), {
      id: call.last_inbound_cdr_id,
      start_time: call.start_dt ? new Date(call.start_dt) : undefined,
      duration: 0,
      voicemail_public_id: call.file_entry_ready && call.sound_id, // Last voice mail message
      is_missed: 1,
      user_direction: CallDirection.INBOUND,
      missed_call_id: call.id,
      message_id: call.message_id, // Last voice mail message
      recall_request_status: call.recall_request_status,
      recall_time: call.recall_time,
    });
    // this.loggerService.log('Detail missed call', callLog);

    this.dialogService.openDialog2(
      CallLogDetail,
      {
        data: {
          call: callLog,
          onMakeCall: this.onMakeCall
        },
        padding: false
      }
    );
  }

  makeCall(item: ToDoCallItem): void {
    const ccConnected: boolean = this.agentPanelService.ccConnected();
    // Agent cannot make call when not connected
    if (!ccConnected) {
      this.dialogService.confirmDialog('Call Center', 'You must connect to Call Center before making a call', () => { });

      return;
    }
    this.phoneService.makeCall(item.calling_number, item.called_number, item.queue_id);
  }

  async updateListened(item: ToDoCallItem): Promise<void> {
    if (item.is_read && _.includes(item.readers || [], this.userId)) {
      // this.loggerService.log('Voice mail has been already listened');
      return;
    }
    await this.em.getRepository<VoiceMailRepository>('VoiceMailRepository').markVoiceMailListened(item.sound_id);
    item.is_read = 1;
    if (!item.readers) {
      item.readers = [];
    }
    item.readers.push(this.userId);
    this.cdr.markForCheck();
  }

  private getFilters(): {
    start_dt?: DateTime,
    end_dt?: DateTime,
    phone_number?: string,
    queue_ids?: number[]
  } {
    const formData = this.formService.normalizeData(this.filterForm.value);
    const filters = {
      start_dt: formData.start_dt,
      end_dt: formData.end_dt,
      phone_number: formData.phone_number && formData.phone_number.toLowerCase() || '',
      queue_ids: formData.queue_ids || [],
    };

    if (filters.start_dt) {
      filters.start_dt = parseDateTime(filters.start_dt);
    }

    if (filters.end_dt) {
      filters.end_dt = parseDateTime(filters.end_dt);
    }

    return filters;
  }

  async applyFilter(): Promise<void> {
    if (!this.filterForm.valid) {
      this.filterForm.markAllAsTouched();
      return;
    }

    await this.loadDataSource();
    this.filterReady = false;
  }

  rendererQueue(queueIds: number[]): string[] {
    return this.queueRepo.getObjectList().filter(q => _.includes(queueIds, q.id)).map(q => q.queue_name);
  }

  clearValue(event: MouseEvent, controlName: string, multiple: boolean = false): void {
    event.stopPropagation();
    this.filterForm.get(controlName).setValue(multiple ? [] : null);
  }
}
